import React, { useState } from 'react';
import './index.scss';

type SceneItem = { title: string; description: string; kind?: 'svg' | 'operation'; image: string };

const frameworkImg = '/images/aip/ProcurementandSales/framework.svg';
const collaborationImg = '/images/aip/ProcurementandSales/collaboration.svg';
const riskControlImg = '/images/aip/ProcurementandSales/risk-control.svg';
const businessFinanceImg = '/images/aip/ProcurementandSales/business-finance.svg';
const operationImg = '/images/aip/ProcurementandSales/unified-operation.svg';

const collaborationScenes: SceneItem[] = [
  {
    title: '采销一体联动',
    description: '销售订单自动驱动采购执行，实现“以销定采”，减少库存压力。',
    image: collaborationImg
  },
  {
    title: '多模式支持',
    description: '支持常规贸易、背靠背贸易、内部多级贸易等多种业务模式。',
    image: collaborationImg
  },
  {
    title: '上下游实时协同',
    description: '供应商与客户在线协同，订单、交付、结算全程可视。',
    image: collaborationImg
  },
  {
    title: '流程自动化',
    description: '订单、交货、结算自动流转，减少人工操作与错误。',
    image: collaborationImg
  }
];

const riskControlScenes: SceneItem[] = [
  {
    title: '资质准入管控',
    description: '自动校验客户与供应商资质，保障业务合规开展。',
    image: riskControlImg
  },
  {
    title: '信用风险控制',
    description: '授信额度与应收应付实时监控，降低坏账风险。',
    image: riskControlImg
  },
  {
    title: '资金与支付管控',
    description: '灵活支付规则与逾期管理，确保资金安全。',
    image: riskControlImg
  },
  {
    title: '价格波动应对',
    description: '支持跌价追保机制，降低市场波动带来的风险。',
    image: riskControlImg
  }
];

const businessFinanceScenes: SceneItem[] = [
  {
    title: '自动对账与结算',
    description: '基于订单与交付自动生成结算单与应收应付。',
    image: businessFinanceImg
  },
  {
    title: '实时数据同步',
    description: '业务数据与财务数据一致，减少数据错误。',
    image: businessFinanceImg
  },
  {
    title: '存货核算能力',
    description: '支持库存价值、采购成本、销售成本核算。',
    image: businessFinanceImg
  },
  {
    title: '全过程可追溯',
    description: '全流程数据留痕，支持审计与追溯。',
    image: businessFinanceImg
  }
];

const operationScenes: SceneItem[] = [
  {
    title: '主数据统一管理',
    description: '物料、客商数据标准化，支持多组织共享与权限控制。',
    kind: 'operation',
    image: operationImg
  },
  {
    title: '灵活定价体系',
    description: '支持按客户、渠道、数量、金额等多维度定价。',
    kind: 'operation',
    image: operationImg
  },
  {
    title: '客商全生命周期管理',
    description: '从准入、运营到评估的全流程管理。',
    kind: 'operation',
    image: operationImg
  },
  {
    title: '端到端协同',
    description: '提供协同门户以及后台工作台，提升供应链协同效率。',
    kind: 'operation',
    image: operationImg
  }
];

const aiCards = [
  {
    title: 'AI合同智能管理',
    description:
      '在合同起草与签署阶段，自动提炼合同要素、识别条款缺项与风险点，并支持纸质与电子合同智能比对，显著提升审核效率并降低合规风险。'
  },
  {
    title: 'AI销售订单助理',
    description:
      '从附件、截图、邮件等非结构化信息中自动识别客户需求并生成订单草稿，减少人工录入时间与错误率，推动订单处理标准化。'
  },
  {
    title: 'AI异常预警与风控',
    description: '对价格偏离、资质到期、信用超额、逾期支付等关键风险信号进行实时预警，辅助业务人员提前干预并优化风控策略。'
  },
  {
    title: 'AI管理驾驶舱',
    description: '通过统一驾驶舱聚合关键经营指标与业务状态，面向战略层和管理层提供可视化洞察与决策支持，提升组织协同效率。'
  }
];

const case1 = '/images/aip/ProcurementandSales/foran-energy-case.jpg';
const cases = [
  {
    id: 'foran-energy',
    href: 'https://terminus.io/case-detail/40147',
    img: case1,
    imgAlt: '佛燃能源案例配图',
    title: '佛燃能源 | 中国服务业企业&能源500强',
    description:
      '以新一代ERP构建采销一体化业务平台，打通供应商、客户、合同、订单、支付等核心数据，形成业务协同与管理驾驶舱联动，实现物料溯源、订单协同、仓储物流等多场景一体化运营。'
  }
  // {
  //   id: 'china-gas',
  //   href: 'https://terminus.io/case-detail/12079',
  //   img: '/images/4.jpg',
  //   imgAlt: '中国燃气案例配图',
  //   title: '中国燃气 ｜ 跨区域综合能源供应及服务企业',
  //   description:
  //     '围绕天然气供应链构建全面贸易风险管控体系，打通财务、OA等系统，提供PC与移动端协同能力，贸易业务线上化、标准化，实现集团高效运营。'
  // }
];

function SceneTabs({ items, defaultIndex = 0 }: { items: SceneItem[]; defaultIndex?: number }) {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const active = items[activeIndex] || items[0];
  const isOperationScene = active.kind === 'operation';

  return (
    <div className="scene-pair">
      <div className="scene-nav">
        {items.map((item, index) => (
          <article
            key={item.title}
            className={`scene-nav-item ${index === activeIndex ? 'is-active' : ''}`}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => setActiveIndex(index)}
          >
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
      <div className={`scene-viewer ${isOperationScene ? 'scene-viewer--operation' : 'scene-viewer--plain'}`}>
        {isOperationScene ? (
          <div className="portal-split" aria-label="统一运营协同门户示意图">
            <article className="portal-card">
              <h4>供应商门户</h4>
              <img src="/images/aip/ProcurementandSales/supplier-portal.png" alt="供应商门户示意图" loading="lazy" />
            </article>
            <article className="portal-card">
              <h4>销售商城</h4>
              <img src="/images/aip/ProcurementandSales/sales-mall.png" alt="销售商城示意图" loading="lazy" />
            </article>
          </div>
        ) : (
          <div className="scene-svg" role="img" aria-label={`${active.title}示意图`}>
            <img src={active.image} alt={`${active.title}示意图`} loading="lazy" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProcurementandSales() {
  return (
    <div className="procurement-and-sales-page">
      <main>
        <section className="hero section">
          <div className="container hero-grid">
            <div className="hero-content">
              <h1>采销协同解决方案</h1>
              <p>
                针对企业常常面对的上游价格波动、下游需求碎片化与跨组织协同断点等问题，端点设计了一套覆盖采购、销售、库存、财务的协同平台。以主数据贯通、流程协同化、风控前置与业财一体为核心，帮助企业从“人工驱动”走向“数据驱动”，实现效率提升、风险可控与经营增长。
              </p>
            </div>
            <aside className="hero-card" aria-label="方案价值摘要">
              <div className="hero-kpi">
                <div>
                  <b>1个</b>
                  <span>统一采销协同平台</span>
                </div>
                <div>
                  <b>4大</b>
                  <span>核心能力</span>
                </div>
                <div>
                  <b>全链路</b>
                  <span>覆盖采购销售库存结算</span>
                </div>
                <div>
                  <b>AI赋能</b>
                  <span>深度融入业务流程</span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="section section-alt" id="framework">
          <div className="container">
            <div className="section-head">
              <h2>采销协同一体化架构</h2>
              <p className="section-head-lines-28">
                整合采购、销售、库存、财务等核心系统，打破数据孤岛，
                <br />
                实现企业内部与上下游伙伴的实时协同，构建端到端供应链闭环。
              </p>
            </div>
            <div className="fw-stack">
              <div className="fw-overview" aria-label="建设框架总览图">
                <div className="fw-svg" role="img" aria-label="采销协同一体化架构图">
                  <img src={frameworkImg} alt="采销协同一体化架构图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="collaboration">
          <div className="container">
            <div className="section-head">
              <h2>供销协同</h2>
              <p>围绕采购与销售核心业务，通过订单驱动供应链执行，实现从需求、下单到交付、结算的全流程协同。</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={collaborationScenes} />
            </div>
          </div>
        </section>

        <section className="section section-alt" id="risk-control">
          <div className="container">
            <div className="section-head">
              <h2>风险管控</h2>
              <p>通过系统规则进行前置校验与过程控制，提升业务的合规性和可控性。</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={riskControlScenes} />
            </div>
          </div>
        </section>

        <section className="section" id="business-finance">
          <div className="container">
            <div className="section-head">
              <h2>业财一体</h2>
              <p>以订单与库存为核心，自动生成应收、应付及结算数据，打通业务系统与财务系统。</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={businessFinanceScenes} />
            </div>
          </div>
        </section>

        <section className="section section-alt" id="operation">
          <div className="container">
            <div className="section-head">
              <h2>统一运营</h2>
              <p>统一管理物料、价格、客户与供应商等核心主数据，支撑采购与销售业务标准化执行。</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={operationScenes} />
            </div>
          </div>
        </section>

        <section className="section" id="ai">
          <div className="container">
            <div className="section-head">
              <h2>AI能力应用</h2>
              <p>将AI深度融入业务流程，从订单处理到合同管理，再到经营决策，全面提升企业智能化水平。</p>
            </div>
            <div className="ai-layout">
              <div className="ai-cards">
                {aiCards.map((card) => (
                  <article key={card.title} className="adv-card">
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="cases">
          <div className="container">
            <div className="section-head">
              <h2>实践案例</h2>
              <p>已在多个企业进行落地，并支撑企业业务发展。</p>
            </div>
            <div className={`case-grid ${cases.length === 1 ? 'case-grid--one' : 'case-grid--two'}`}>
              {cases.map((item) => (
                <a key={item.id} className="case-link" href={item.href} target="_blank" rel="noopener noreferrer">
                  <article className="case-card">
                    <div className="case-media-placeholder">
                      <img src={item.img} alt={item.imgAlt} />
                    </div>
                    <h3>{item.title}</h3>
                    <p className="case-one-line">{item.description}</p>
                  </article>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

